import React, { useState, useEffect } from "react";
import {
  SafeAreaView,
  StyleSheet,
  View,
  ActivityIndicator,
  Text,
  Platform,
  TextInput,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
} from "react-native";
import { router } from "expo-router";
import { Colors } from "@/constants/Colors";
import Header from "@/components/ui/Header";
import { FontFamilies } from "@/constants/Typography";
import { STRIPE_PUBLISHABLE_KEY } from "@/constants/Configs";
import StripePaymentServices from "@/services/stripePaymentServices";
import { showError, showSuccess } from "@/utils/toast";
import useUserStore from "@/store/user";

// Web-only imports
let loadStripe: any;
let Elements: any;
let CardNumberElement: any;
let CardExpiryElement: any;
let CardCvcElement: any;
let useStripe: any;
let useElements: any;

if (Platform.OS === "web") {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const stripeJs = require("@stripe/stripe-js");
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const stripeReact = require("@stripe/react-stripe-js");
  loadStripe = stripeJs.loadStripe;
  Elements = stripeReact.Elements;
  CardNumberElement = stripeReact.CardNumberElement;
  CardExpiryElement = stripeReact.CardExpiryElement;
  CardCvcElement = stripeReact.CardCvcElement;
  useStripe = stripeReact.useStripe;
  useElements = stripeReact.useElements;
}

// Stripe promise (web only)
const stripePromise =
  Platform.OS === "web" ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

// Card form component for web
const CardForm = ({
  clientSecret,
  onSuccess,
}: {
  clientSecret: string;
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardholderName, setCardholderName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardComplete, setCardComplete] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  });

  const isFormValid =
    cardholderName.trim().length > 0 &&
    cardComplete.cardNumber &&
    cardComplete.cardExpiry &&
    cardComplete.cardCvc;

  const handleSubmit = async () => {
    if (!stripe || !elements || !isFormValid) return;

    setIsSubmitting(true);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);

      const { error, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              name: cardholderName,
            },
          },
        }
      );

      if (error) {
        showError(error.message || "Failed to add card");
        setIsSubmitting(false);
        return;
      }

      if (setupIntent?.status === "succeeded") {
        // Set the new card as default (also retries payment if paymentFailed)
        const result =
          await StripePaymentServices.setDefaultPaymentMethodFromSetupIntent(
            setupIntent.id
          );

        if (result.data?.paymentRetried) {
          if (result.data?.paymentSucceeded) {
            showSuccess("Card added and payment successful!");
          } else {
            showError(
              "Card added but payment failed. Please try a different card."
            );
          }
        } else {
          showSuccess("Card added successfully");
        }
        onSuccess();
      }
    } catch (err) {
      console.error("Error adding card:", err);
      showError("Failed to add card");
    } finally {
      setIsSubmitting(false);
    }
  };

  const cardElementStyle = {
    base: {
      fontSize: "15px",
      fontFamily: "Inter, sans-serif",
      color: "#161D14",
      "::placeholder": {
        color: "#A3A3A3",
      },
    },
    invalid: {
      color: "#C12915",
    },
  };

  return (
    <KeyboardAvoidingView
      style={styles.formContainer}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.form}>
          {/* Card Number */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Card Number</Text>
            <View style={styles.stripeInputContainer}>
              <CardNumberElement
                options={{
                  style: cardElementStyle,
                  showIcon: true,
                }}
                onChange={(event: any) =>
                  setCardComplete((prev) => ({
                    ...prev,
                    cardNumber: event.complete,
                  }))
                }
              />
            </View>
          </View>

          {/* Exp Date and CVV Row */}
          <View style={styles.row}>
            <View style={[styles.inputGroup, styles.halfWidth]}>
              <Text style={styles.label}>Exp Date</Text>
              <View style={styles.stripeInputContainer}>
                <CardExpiryElement
                  options={{
                    style: cardElementStyle,
                    placeholder: "MM/YY",
                  }}
                  onChange={(event: any) =>
                    setCardComplete((prev) => ({
                      ...prev,
                      cardExpiry: event.complete,
                    }))
                  }
                />
              </View>
            </View>

            <View style={[styles.inputGroup, styles.halfWidth]}>
              <Text style={styles.label}>CVV</Text>
              <View style={styles.stripeInputContainer}>
                <CardCvcElement
                  options={{
                    style: cardElementStyle,
                    placeholder: "123",
                  }}
                  onChange={(event: any) =>
                    setCardComplete((prev) => ({
                      ...prev,
                      cardCvc: event.complete,
                    }))
                  }
                />
              </View>
            </View>
          </View>

          {/* Cardholder Name */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cardholder Name</Text>
            <TextInput
              style={styles.textInput}
              value={cardholderName}
              onChangeText={setCardholderName}
              placeholder="Your name"
              placeholderTextColor="#A3A3A3"
              autoCapitalize="words"
              autoCorrect={false}
            />
          </View>
        </View>

        {/* Submit Button */}
        <TouchableOpacity
          style={[
            styles.submitButton,
            (!isFormValid || isSubmitting) && styles.submitButtonDisabled,
          ]}
          onPress={handleSubmit}
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? (
            <ActivityIndicator size="small" color="#A3A3A3" />
          ) : (
            <Text
              style={[
                styles.submitButtonText,
                (!isFormValid || isSubmitting) &&
                  styles.submitButtonTextDisabled,
              ]}
            >
              Continue
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const AddCardScreen = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const { getUserProfile } = useUserStore();

  useEffect(() => {
    const initAddCard = async () => {
      try {
        const res = await StripePaymentServices.createSetupIntent();

        if (res.success && res.data.clientSecret) {
          setClientSecret(res.data.clientSecret);
        } else {
          showError("Failed to initialize card setup");
          router.back();
        }
      } catch (err) {
        console.error("Error creating setup intent:", err);
        showError("Failed to initialize card setup");
        router.back();
      } finally {
        setIsLoading(false);
      }
    };

    if (Platform.OS === "web") {
      initAddCard();
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleSuccess = async () => {
    await getUserProfile();
    router.back();
  };

  const handleCancel = () => {
    router.back();
  };

  // Mobile platforms - not available (use IAP)
  if (Platform.OS !== "web") {
    return (
      <SafeAreaView style={styles.container}>
        <Header title="Add Card" />
        <View style={styles.notAvailableContainer}>
          <Text style={styles.notAvailableText}>
            Adding payment cards is not available on this platform.
          </Text>
          <Text style={styles.notAvailableSubtext}>
            Please use the web version to manage your payment methods.
          </Text>
          <TouchableOpacity style={styles.backButton} onPress={handleCancel}>
            <Text style={styles.backButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title="Add card" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Colors.primary} />
          <Text style={styles.loadingText}>Preparing card setup...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!clientSecret) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title="Add card" />
        <View style={styles.loadingContainer}>
          <Text style={styles.errorText}>Failed to load card setup</Text>
          <TouchableOpacity style={styles.backButton} onPress={handleCancel}>
            <Text style={styles.backButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Add card" />
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: Colors.primary,
              fontFamily: "Inter, sans-serif",
            },
          },
        }}
      >
        <CardForm
          clientSecret={clientSecret}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </Elements>
    </SafeAreaView>
  );
};

export default AddCardScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Regular,
    color: Colors.text,
  },
  errorText: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Regular,
    color: Colors.error,
    textAlign: "center",
    marginBottom: 16,
  },
  notAvailableContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  notAvailableText: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: Colors.text,
    textAlign: "center",
    marginBottom: 8,
  },
  notAvailableSubtext: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
    textAlign: "center",
    marginBottom: 24,
  },
  formContainer: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
    justifyContent: "space-between",
  },
  form: {
    gap: 16,
  },
  inputGroup: {
    gap: 2,
  },
  label: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    marginBottom: 2,
  },
  row: {
    flexDirection: "row",
    gap: 16,
  },
  halfWidth: {
    flex: 1,
  },
  stripeInputContainer: {
    backgroundColor: "#EBEBEB",
    borderWidth: 1,
    borderColor: "#EBEBEB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    minHeight: 44,
    justifyContent: "center",
  },
  textInput: {
    backgroundColor: "#EBEBEB",
    borderWidth: 1,
    borderColor: "#EBEBEB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    minHeight: 44,
  },
  submitButton: {
    backgroundColor: Colors.primary,
    borderRadius: 100,
    paddingVertical: 20,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 24,
    marginBottom: 32,
  },
  submitButtonDisabled: {
    backgroundColor: "#EBEBEB",
  },
  submitButtonText: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#FFFFFF",
  },
  submitButtonTextDisabled: {
    color: "#A3A3A3",
  },
  backButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  backButtonText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: Colors.primary,
  },
});
